// WorksheetWizardPage — 5-step worksheet intake accordion (E-105 Phase 2 S5 MVP)
// Flow: Partner → Javítás típusa → Gépkategória → Gép → Hiba → Mentés(FELVÉVE) → Auto-Print
// Diagnosztika a WorksheetDetailPage-ről érhető el később.
// ADR-103 accordion pattern — WizardPanel a @kgc/ui-ból (ugyanaz a komponens mint
// a RentalWizardPage-en). Drift dual-gate + i18n Phase 3-ban.

import { WizardShell } from '@/components/layout/WizardShell';
import { Button, Card } from '@/components/ui';
import { useEffectivePackage } from '@/hooks/use-effective-package';
import { useWorksheetMutations } from '@/hooks/use-worksheets';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import type { PackageInfoPanelPackage } from '@kgc/ui';
import { Badge, PackageInfoPanel, Skeleton, WizardPanel, handleWizardPanelKeyNav } from '@kgc/ui';
import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { MachineCategoryStep, PartnerStep, ProblemStep, ProductStep } from './steps';
import { RepairTypeStep } from './steps/RepairTypeStep';
import type { WorksheetType } from './types';
import { useWorksheetWizardStore, type WorksheetPanelId } from './wizard-store';

interface PanelDescriptor {
  id: WorksheetPanelId;
  stepNumber: number;
  titleKey: string;
  renderStep: () => ReactNode;
}

export function WorksheetWizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    openPanels,
    togglePanel,
    getPanelValidity,
    partner,
    product,
    deviceName,
    serialNumber,
    worksheetType,
    repairType,
    priority,
    faultDescription,
    overrideExpiredId,
    overrideReason,
    reset,
  } = useWorksheetWizardStore();

  const { createWorksheet, isLoading: isSubmitting } = useWorksheetMutations();

  const machineCategoryId = useWorksheetWizardStore(s => s.machineCategoryId);

  // E-105 Phase 2 S5 MVP — resolver happy-path integráció
  const { effectivePackage, isLoading: isPackageLoading } = useEffectivePackage({
    productId: product?.id,
    machineCategoryId: machineCategoryId ?? undefined,
    packageType: 'SERVICE',
  });

  const resolvedPanelPackage: PackageInfoPanelPackage | null = effectivePackage
    ? {
        id: effectivePackage.id,
        packageCode: effectivePackage.packageCode,
        name: effectivePackage.name,
        type: effectivePackage.type as PackageInfoPanelPackage['type'],
        scope: effectivePackage.scope as PackageInfoPanelPackage['scope'],
        validFrom: effectivePackage.validFrom,
        validTo: effectivePackage.validTo,
      }
    : null;

  // C3 fix: static panel descriptors — renderStep NEM záródik reaktív értékekre.
  // A product panel resolver-szekciója külön renderelődik a renderPanel-ben.
  const panels: PanelDescriptor[] = useMemo(
    () => [
      {
        id: 'partner',
        stepNumber: 1,
        titleKey: 'table.partner',
        renderStep: () => <PartnerStep />,
      },
      {
        id: 'repairType',
        stepNumber: 2,
        titleKey: 'worksheet.repairType.title',
        renderStep: () => <RepairTypeStep />,
      },
      {
        id: 'machineCategory',
        stepNumber: 3,
        titleKey: 'worksheet.machineCategory.title',
        renderStep: () => <MachineCategoryStep />,
      },
      {
        id: 'product',
        stepNumber: 4,
        titleKey: 'service.device',
        renderStep: () => <ProductStep />,
      },
      {
        id: 'problem',
        stepNumber: 5,
        titleKey: 'service.fault',
        renderStep: () => <ProblemStep />,
      },
    ],
    []
  );

  const panelValidities = panels.map(p => getPanelValidity(p.id));
  const validCount = panelValidities.filter(v => v.isValid).length;
  const allValid = validCount === panels.length;
  const canSubmit = allValid && (!overrideExpiredId || overrideReason.trim().length > 0);

  const handleComplete = async () => {
    if (!canSubmit) return;
    try {
      const result = await createWorksheet({
        partnerId: partner?.id ?? '',
        deviceName: product?.name ?? deviceName,
        deviceSerialNumber: product?.serialNumber ?? serialNumber,
        faultDescription: faultDescription,
        type: (repairType ?? worksheetType) as WorksheetType,
        priority: priority as 'NORMAL' | 'SURGOS' | 'FELARAS' | 'GARANCIALIS' | 'FRANCHISE',
        // A store KÓDOT tárol (`MachineCategoryStep`: `machineCategoryId === option.code`),
        // és a `#1946` DTO-ja pont kódot vár — nincs FE-oldali UUID-váltás.
        // Kategória nélkül a kulcs ki sem kerül: a mező opcionális, elhagyva a BE
        // viselkedése változatlan.
        ...(machineCategoryId ? { machineCategoryCode: machineCategoryId } : {}),
        ...(overrideExpiredId ? { overrideExpiredId: true } : {}),
        ...(overrideExpiredId && overrideReason.trim().length > 0
          ? { overrideReason: overrideReason.trim() }
          : {}),
      });
      reset();
      navigate(`/worksheet/${result.id}?print=true`);
    } catch {
      // Error handled by hook
    }
  };

  const handleCancel = () => {
    if (confirm(t('service.cancelIntakeConfirm'))) {
      reset();
      navigate('/dashboard');
    }
  };

  // ─── Header ───────────────────────────────────────────────────────────────
  const headerNode = (
    <header className="border-b bg-white shadow-sm dark:bg-[#1a4f5c] dark:border-[#3a9aa8]">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-2 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleCancel}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:border-[#3a9aa8] dark:text-[#99f6e4] dark:hover:bg-[#1f5f6b] dark:hover:text-white"
            aria-label={t('common.back')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <div>
            <h1 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
              {t('service.worksheetIntake')}
            </h1>
            <p className="text-[11px] text-gray-400 dark:text-[#99f6e4] leading-tight">
              {t('service.newServiceWorksheet')}
            </p>
          </div>
        </div>

        <nav
          className="flex flex-1 items-center justify-center"
          aria-label="Worksheet wizard progress"
        >
          {panels.map((p, idx) => {
            const isValid = panelValidities[idx]?.isValid ?? false;
            return (
              <div key={p.id} className="flex items-center">
                <div className="flex items-center gap-1.5">
                  <div
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition-colors',
                      isValid
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600 dark:bg-[#2d7d8a] dark:text-white'
                    )}
                  >
                    {isValid ? (
                      <svg
                        className="h-4 w-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      p.stepNumber
                    )}
                  </div>
                  <span
                    className={cn(
                      'hidden text-xs font-medium sm:inline',
                      isValid
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-400 dark:text-[#99f6e4]'
                    )}
                  >
                    {t(p.titleKey)}
                  </span>
                </div>
                {idx < panels.length - 1 && (
                  <div
                    className={cn(
                      'mx-1.5 h-0.5 w-6 sm:w-10 rounded-full',
                      isValid ? 'bg-green-400' : 'bg-gray-300 dark:bg-[#3a9aa8]'
                    )}
                  />
                )}
              </div>
            );
          })}
        </nav>

        <Button
          variant="outline"
          size="sm"
          onClick={handleCancel}
          className="shrink-0 text-xs border-gray-300 dark:border-[#3a9aa8] dark:text-[#ccfbf1]"
        >
          {t('common.cancel')}
        </Button>
      </div>
    </header>
  );

  // ─── Footer ───────────────────────────────────────────────────────────────
  const footerNode = (
    <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 sm:px-6 lg:px-8">
      <div className="text-sm text-gray-500 dark:text-[#ccfbf1]">
        {validCount} / {panels.length} kész
      </div>
      <Button
        onClick={handleComplete}
        disabled={isSubmitting || !canSubmit}
        className="bg-green-600 hover:bg-green-700"
        data-testid="worksheet-wizard-submit"
      >
        {isSubmitting ? (
          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent" />
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="mr-2 h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
            />
          </svg>
        )}
        {isSubmitting ? t('service.creating') : t('service.worksheetIntakeAndPrint')}
      </Button>
    </div>
  );

  // ─── Panel renderer ───────────────────────────────────────────────────────
  const renderPanel = (descriptor: PanelDescriptor, idx: number): ReactNode => {
    const validity = panelValidities[idx] ?? { isValid: false };
    return (
      <WizardPanel
        key={descriptor.id}
        id={descriptor.id}
        namespace="worksheet-panel"
        title={t(descriptor.titleKey)}
        stepNumber={descriptor.stepNumber}
        isOpen={openPanels.has(descriptor.id)}
        isValid={validity.isValid}
        onToggle={() => togglePanel(descriptor.id)}
        // a11y: a panel-cím h2 (page h1 → panel h2 → step h3) — nincs h1→h3 skip
        // az accordion heading-wrapped header-ben (#209-osztály, WizardPanel +headingLevel).
        headingLevel="h2"
        {...(validity.summary ? { summary: validity.summary } : {})}
      >
        <Card className="p-4">
          {descriptor.renderStep()}
          {/* C3 fix: resolver szekció a product panel-ben — friss closure, nem useMemo-ban */}
          {descriptor.id === 'product' && (
            <ResolvedPackageSection
              pkg={resolvedPanelPackage}
              isLoading={isPackageLoading}
              hasProduct={!!product}
            />
          )}
        </Card>
      </WizardPanel>
    );
  };

  return (
    <WizardShell mode="custom" stepper={headerNode} footer={footerNode}>
      <div
        className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 space-y-3"
        onKeyDown={handleWizardPanelKeyNav}
      >
        {panels.map((p, idx) => renderPanel(p, idx))}
      </div>
    </WizardShell>
  );
}

// ─── Resolved Package Section ─────────────────────────────────────────────

function ResolvedPackageSection({
  pkg,
  isLoading,
  hasProduct,
}: {
  pkg: PackageInfoPanelPackage | null;
  isLoading: boolean;
  hasProduct: boolean;
}) {
  const { t } = useTranslation();
  if (!hasProduct) return null;

  if (isLoading) {
    return (
      <div className="mt-4 space-y-2" data-testid="resolved-package-loading">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (!pkg) {
    return (
      <div
        className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"
        data-testid="resolved-package-none"
      >
        <Badge variant="outline">{t('worksheet.resolvedPackage.none')}</Badge>
        <span>{t('worksheet.resolvedPackage.noneDesc')}</span>
      </div>
    );
  }

  return (
    <div className="mt-4" data-testid="resolved-package-info">
      <PackageInfoPanel package={pkg} />
    </div>
  );
}
